/**
 * Hook V2 — useApprenantContrat
 *
 * Phase wiring 2 : branche sur `GET /api/v1/v2/apprenants/:id/contrat`.
 * Fallback mock si API indisponible.
 *
 * Source : Zoho Apprenant + Inscriptions (workflow inféré selon Type_de_financement).
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '../../../lib/api.js';

export type FinanceurType = 'CPF' | 'OPCO' | 'ST_ENTRANTE' | 'ST_SORTANTE' | 'DIRECT';
export type JalonStatus = 'à_venir' | 'en_attente_action' | 'envoyé' | 'validé' | 'payé';

export interface JalonContrat {
  label: string;
  status: JalonStatus;
  expectedDate: string | null;
  realizedDate: string | null;
  amount: number;
  condition: string | null;
  actionOFRequired: string | null;
}

export interface ApprenantContrat {
  apprenantId: string;
  conventionSigneeAt: string | null;
  conventionDriveUrl: string | null;
  conventionGeneratedAt: string | null;
  financeurType: FinanceurType;
  financeurName: string;
  montantTotal: number;
  jalonsCount: { realized: number; total: number };
  jalons: JalonContrat[];
  specificites: string[];
}

const MARTINE_MOCK: ApprenantContrat = {
  apprenantId: 'demo-martine',
  conventionSigneeAt: '2025-04-13',
  conventionDriveUrl: 'https://drive.google.com/drive/folders/martine-convention',
  conventionGeneratedAt: '2025-04-12',
  financeurType: 'CPF',
  financeurName: 'Caisse des Dépôts (CPF)',
  montantTotal: 5100,
  jalonsCount: { realized: 0, total: 1 },
  jalons: [
    {
      label: 'Paiement CPF post-formation',
      status: 'à_venir',
      expectedDate: '2026-07-15',
      realizedDate: null,
      amount: 5100,
      condition: 'Attestation de fin de formation déposée sur EDOF',
      actionOFRequired: 'Déposer attestation + certif sur EDOF dans 4 semaines après fin',
    },
  ],
  specificites: [
    'Financement CPF intégral',
    'Aucun reste à charge apprenant',
    'Délai paiement Caisse des Dépôts : 30 à 60 jours après dépôt EDOF',
  ],
};

async function fetchContrat(id: string): Promise<ApprenantContrat & { source: 'api' | 'mock' }> {
  try {
    const data = await api.get<ApprenantContrat>(`/api/v1/v2/apprenants/${id}/contrat`);
    return { ...data, source: 'api' };
  } catch (err) {
    if (err instanceof ApiClientError && [401, 404, 501, 0].includes(err.status)) {
      return { ...MARTINE_MOCK, source: 'mock' };
    }
    return { ...MARTINE_MOCK, source: 'mock' };
  }
}

export function useApprenantContrat(id: string): {
  data: (ApprenantContrat & { source: 'api' | 'mock' }) | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ['v2', 'apprenant-contrat', id],
    queryFn: () => fetchContrat(id),
    staleTime: 60_000,
  });
  return { data: data ?? null, isLoading, error: (error as Error | null) ?? null };
}
